import * as THREE from 'three';
import { WeaponDef } from '../types';
import { Target, TargetConfig, TargetKind } from './targets';
import { DESERT_SPAWN_SLOTS, buildDesertRange } from './maps/desertRange';
import { GameAudio } from './audio';
import { buildViewmodel } from './viewmodel';

const INITIAL_TIME_MS = 30_000;
const MAX_TIME_MS = 45_000;
const HIT_TIME_BONUS_MS = 350;
const MISS_PENALTY_MS = 1_200;
const EXPIRE_PENALTY_MS = 1_600;
const COMBO_CAP = 20;
const DRONE_INTERVAL_MS = 26_000;

export interface HudState {
  score: number;
  timeLeftMs: number;
  ammo: number;
  magazineSize: number;
  reloading: boolean;
  combo: number;
  weaponName: string;
}

export interface FloatingTextEvent {
  screenX: number;
  screenY: number;
  text: string;
  kind: 'hit' | 'miss' | 'penalty' | 'bonus';
}

export interface MatchResultInternal {
  score: number;
  shots: number;
  hits: number;
  durationSeconds: number;
}

export interface GameCallbacks {
  onHud: (hud: HudState) => void;
  onFloatingText: (e: FloatingTextEvent) => void;
  onGameOver: (result: MatchResultInternal) => void;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * Math.max(0, Math.min(1, t));
}

function difficultyAt(elapsedSeconds: number) {
  const t = Math.min(elapsedSeconds / 90, 1);
  return {
    spawnIntervalMs: lerp(1300, 420, t),
    maxSimultaneous: Math.min(5, 1 + Math.floor(elapsedSeconds / 18)),
    lifetimeMs: lerp(2200, 850, t),
    scale: lerp(1.0, 0.55, t),
    driftChance: lerp(0.15, 0.6, t),
    pointsMultiplier: lerp(1.0, 1.6, t),
  };
}

export class Game {
  private scene = new THREE.Scene();
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private container: HTMLElement;
  private raycaster = new THREE.Raycaster();
  private clock = new THREE.Clock();
  private audio = new GameAudio();

  private weapon: WeaponDef;
  private callbacks: GameCallbacks;

  private targets: Target[] = [];
  private occupiedSlots = new Set<number>();

  private score = 0;
  private shots = 0;
  private hits = 0;
  private timeLeftMs = INITIAL_TIME_MS;
  private elapsedMs = 0;
  private combo = 0;
  private ammo: number;
  private reloading = false;
  private lastShotAt = -Infinity;
  private nextSpawnAt = 0;
  private nextDroneAt = DRONE_INTERVAL_MS;
  private gameOver = false;

  private shakeIntensity = 0;
  private basePosition = new THREE.Vector3(0, 1.65, 0);
  private mouseNDC = new THREE.Vector2(0, 0);

  private viewmodel: THREE.Group | null = null;
  private viewmodelRestPos = new THREE.Vector3(0.35, -0.32, -0.55);
  private viewmodelRecoil = 0;

  private animHandle = 0;
  private boundResize: () => void;
  private boundClick: (e: MouseEvent) => void;
  private boundMove: (e: MouseEvent) => void;
  private boundContextMenu: (e: MouseEvent) => void;

  constructor(container: HTMLElement, weapon: WeaponDef, callbacks: GameCallbacks) {
    this.container = container;
    this.weapon = weapon;
    this.callbacks = callbacks;
    this.ammo = weapon.magazineSize;

    const width = container.clientWidth;
    const height = container.clientHeight;

    this.camera = new THREE.PerspectiveCamera(62, width / height, 0.1, 250);
    this.camera.position.copy(this.basePosition);
    // The range (props, targets, fence) is built out along +Z; point the
    // camera to face +Z instead of Three.js's default -Z facing.
    this.camera.rotation.y = Math.PI;
    // Camera must be part of the scene graph for its child (the viewmodel
    // gun) to be rendered.
    this.scene.add(this.camera);

    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(this.renderer.domElement);

    buildDesertRange(this.scene);

    this.viewmodel = buildViewmodel(weapon);
    this.viewmodel.position.copy(this.viewmodelRestPos);
    this.camera.add(this.viewmodel);

    this.boundResize = this.handleResize.bind(this);
    this.boundClick = this.handleClick.bind(this);
    this.boundMove = this.handleMove.bind(this);
    this.boundContextMenu = (e) => e.preventDefault();

    window.addEventListener('resize', this.boundResize);
    this.renderer.domElement.addEventListener('mousedown', this.boundClick);
    this.renderer.domElement.addEventListener('mousemove', this.boundMove);
    this.renderer.domElement.addEventListener('contextmenu', this.boundContextMenu);

    this.audio.resume();
    this.pushHud();
    this.animHandle = requestAnimationFrame(this.loop.bind(this));
  }

  private handleResize() {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  private handleMove(e: MouseEvent) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouseNDC.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouseNDC.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  }

  private handleClick(e: MouseEvent) {
    if (e.button !== 0 || this.gameOver) return;
    this.fire();
  }

  private pushHud() {
    this.callbacks.onHud({
      score: this.score,
      timeLeftMs: this.timeLeftMs,
      ammo: this.ammo,
      magazineSize: this.weapon.magazineSize,
      reloading: this.reloading,
      combo: this.combo,
      weaponName: this.weapon.name,
    });
  }

  private fire() {
    const now = performance.now();
    if (this.reloading) return;
    if (this.ammo <= 0) {
      this.startReload();
      return;
    }
    if (now - this.lastShotAt < this.weapon.fireIntervalMs) return;
    this.lastShotAt = now;

    this.ammo -= 1;
    this.shots += 1;
    this.audio.shot(this.weapon.category);
    this.shakeIntensity = Math.min(this.shakeIntensity + 0.06, 0.35);
    this.viewmodelRecoil = 1.0;

    let anyHit = false;

    for (let p = 0; p < this.weapon.pelletsPerShot; p++) {
      const spreadRad = (this.weapon.spreadDeg * Math.PI) / 180;
      const jitterX = (Math.random() - 0.5) * spreadRad;
      const jitterY = (Math.random() - 0.5) * spreadRad;

      const ndc = this.mouseNDC.clone();
      ndc.x += jitterX;
      ndc.y += jitterY;

      this.raycaster.setFromCamera(ndc, this.camera);
      const meshes = this.targets.flatMap((t) => [t.group.children[1] as THREE.Mesh]); // disc is index 1
      const intersections = this.raycaster.intersectObjects(meshes, false);

      if (intersections.length > 0) {
        const hitMesh = intersections[0].object;
        const target = this.targets.find((t) => t.group.children[1] === hitMesh);
        if (target && target.state !== 'dead') {
          anyHit = true;
          this.hits += 1;
          this.registerHitOnTarget(target, intersections[0].point);
        }
      }
    }

    if (this.ammo === 0) {
      this.startReload();
    }

    if (!anyHit) {
      this.registerMiss();
    }

    this.pushHud();
  }

  private startReload() {
    this.reloading = true;
    this.audio.reload();
    this.pushHud();
    setTimeout(() => {
      if (this.gameOver) return;
      this.ammo = this.weapon.magazineSize;
      this.reloading = false;
      this.pushHud();
    }, this.weapon.reloadMs);
  }

  private registerHitOnTarget(target: Target, worldPoint: THREE.Vector3) {
    const difficulty = difficultyAt(this.elapsedMs / 1000);
    this.combo = Math.min(this.combo + 1, COMBO_CAP);
    const comboMultiplier = 1 + Math.min(this.combo, COMBO_CAP) * 0.05;
    const points = Math.round(
      this.weapon.basePoints * difficulty.pointsMultiplier * comboMultiplier * target.config.basePointsMultiplier
    );

    const destroyed = target.registerHit();
    this.score += points;
    this.timeLeftMs = Math.min(MAX_TIME_MS, this.timeLeftMs + HIT_TIME_BONUS_MS);

    this.audio.hitMarker();
    this.shakeIntensity = Math.min(this.shakeIntensity + 0.1, 0.4);

    const screen = this.worldToScreen(worldPoint);
    this.callbacks.onFloatingText({
      screenX: screen.x,
      screenY: screen.y,
      text: `+${points}`,
      kind: destroyed && target.config.kind !== 'standard' ? 'bonus' : 'hit',
    });
  }

  private registerMiss() {
    this.combo = 0;
    this.timeLeftMs = Math.max(0, this.timeLeftMs - MISS_PENALTY_MS);
    this.audio.miss();
    this.shakeIntensity = Math.min(this.shakeIntensity + 0.15, 0.5);
    const screen = { x: (this.mouseNDC.x * 0.5 + 0.5) * this.container.clientWidth, y: (-this.mouseNDC.y * 0.5 + 0.5) * this.container.clientHeight };
    this.callbacks.onFloatingText({ screenX: screen.x, screenY: screen.y, text: 'MISS -1.2s', kind: 'miss' });
  }

  private worldToScreen(pos: THREE.Vector3): { x: number; y: number } {
    const v = pos.clone().project(this.camera);
    return {
      x: (v.x * 0.5 + 0.5) * this.container.clientWidth,
      y: (-v.y * 0.5 + 0.5) * this.container.clientHeight,
    };
  }

  private pickKind(elapsedSeconds: number): TargetKind {
    const roll = Math.random();
    if (roll < 0.06) return 'golden';
    if (elapsedSeconds > 20 && roll < 0.2) return 'armored';
    if (elapsedSeconds > 45 && roll < 0.32) return 'small';
    if (Math.random() < difficultyAt(elapsedSeconds).driftChance) return 'moving';
    return 'standard';
  }

  private spawnTarget(kind: TargetKind, forceSlot?: number) {
    const freeSlots = DESERT_SPAWN_SLOTS.map((_, i) => i).filter((i) => !this.occupiedSlots.has(i));
    if (freeSlots.length === 0) return;
    const slotIndex = forceSlot !== undefined && freeSlots.includes(forceSlot)
      ? forceSlot
      : freeSlots[Math.floor(Math.random() * freeSlots.length)];
    const slot = DESERT_SPAWN_SLOTS[slotIndex];
    const difficulty = difficultyAt(this.elapsedMs / 1000);

    const kindDefaults: Record<TargetKind, Partial<TargetConfig>> = {
      standard: { hitsRequired: 1, basePointsMultiplier: 1, driftSpeed: 0 },
      moving: { hitsRequired: 1, basePointsMultiplier: 1.1, driftSpeed: 0.9 + Math.random() * 0.6 },
      small: { hitsRequired: 1, basePointsMultiplier: 1.3, driftSpeed: 0 },
      armored: { hitsRequired: 2, basePointsMultiplier: 1.6, driftSpeed: 0 },
      golden: { hitsRequired: 1, basePointsMultiplier: 2.6, driftSpeed: 0.6 },
      drone: { hitsRequired: 3, basePointsMultiplier: 4, driftSpeed: 1.2 },
    };

    const defaults = kindDefaults[kind];
    const scale = kind === 'small' ? difficulty.scale * 0.6 : kind === 'drone' ? difficulty.scale * 1.3 : difficulty.scale;

    const config: TargetConfig = {
      kind,
      position: new THREE.Vector3(slot.x, 0, slot.z),
      scale,
      standHeight: 1.3,
      lifetimeMs: kind === 'drone' ? difficulty.lifetimeMs * 1.8 : difficulty.lifetimeMs,
      driftSpeed: defaults.driftSpeed ?? 0,
      driftRange: 1.4,
      hitsRequired: defaults.hitsRequired ?? 1,
      basePointsMultiplier: defaults.basePointsMultiplier ?? 1,
    };

    const target = new Target(config);
    this.scene.add(target.group);
    this.targets.push(target);
    this.occupiedSlots.add(slotIndex);
    (target as any)._slotIndex = slotIndex;
  }

  private loop() {
    if (this.gameOver) return;
    const dtSec = Math.min(this.clock.getDelta(), 0.05);
    const dtMs = dtSec * 1000;
    this.elapsedMs += dtMs;

    if (!this.reloading) {
      this.timeLeftMs -= dtMs;
    } else {
      this.timeLeftMs -= dtMs; // clock keeps running during reload too, arcade pressure
    }

    if (this.timeLeftMs <= 0) {
      this.endGame();
      return;
    }

    const difficulty = difficultyAt(this.elapsedMs / 1000);

    if (this.elapsedMs >= this.nextDroneAt) {
      this.spawnTarget('drone');
      this.nextDroneAt = this.elapsedMs + DRONE_INTERVAL_MS;
    } else if (this.elapsedMs >= this.nextSpawnAt && this.targets.length < difficulty.maxSimultaneous) {
      this.spawnTarget(this.pickKind(this.elapsedMs / 1000));
      this.nextSpawnAt = this.elapsedMs + difficulty.spawnIntervalMs;
    }

    for (let i = this.targets.length - 1; i >= 0; i--) {
      const target = this.targets[i];
      const alive = target.update(dtMs);
      if (!alive) {
        const wasHit = target.hitsRemaining <= 0;
        if (!wasHit) {
          this.timeLeftMs = Math.max(0, this.timeLeftMs - EXPIRE_PENALTY_MS);
          this.combo = 0;
          this.audio.targetExpire();
          const screen = this.worldToScreen(target.worldTopPosition);
          this.callbacks.onFloatingText({ screenX: screen.x, screenY: screen.y, text: 'MISSED -1.6s', kind: 'penalty' });
        }
        target.dispose(this.scene);
        this.occupiedSlots.delete((target as any)._slotIndex);
        this.targets.splice(i, 1);
      }
    }

    // Recoil animation: kicks the viewmodel back/up on fire, eases back to rest.
    if (this.viewmodel) {
      this.viewmodelRecoil *= 0.82;
      this.viewmodel.position.z = this.viewmodelRestPos.z + this.viewmodelRecoil * 0.09;
      this.viewmodel.position.y = this.viewmodelRestPos.y + this.viewmodelRecoil * 0.02;
      this.viewmodel.rotation.x = -this.viewmodelRecoil * 0.18;
    }

    // Screen shake decay applied to camera position around the base stance.
    if (this.shakeIntensity > 0.001) {
      this.camera.position.set(
        this.basePosition.x + (Math.random() - 0.5) * this.shakeIntensity,
        this.basePosition.y + (Math.random() - 0.5) * this.shakeIntensity,
        this.basePosition.z
      );
      this.shakeIntensity *= 0.85;
    } else {
      this.camera.position.copy(this.basePosition);
    }

    this.renderer.render(this.scene, this.camera);
    this.pushHud();

    this.animHandle = requestAnimationFrame(this.loop.bind(this));
  }

  private endGame() {
    this.gameOver = true;
    this.audio.gameOver();
    this.callbacks.onGameOver({
      score: this.score,
      shots: this.shots,
      hits: this.hits,
      durationSeconds: Math.round(this.elapsedMs / 1000),
    });
  }

  dispose() {
    this.gameOver = true;
    cancelAnimationFrame(this.animHandle);
    window.removeEventListener('resize', this.boundResize);
    this.renderer.domElement.removeEventListener('mousedown', this.boundClick);
    this.renderer.domElement.removeEventListener('mousemove', this.boundMove);
    this.renderer.domElement.removeEventListener('contextmenu', this.boundContextMenu);
    this.targets.forEach((t) => t.dispose(this.scene));
    this.renderer.dispose();
    if (this.renderer.domElement.parentElement === this.container) {
      this.container.removeChild(this.renderer.domElement);
    }
  }
}
